import { useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { loanService, kycService } from '../../services/loanService';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const LOAN_TYPES = [
  ['personal', 'Personal Loan'], ['business', 'Business Loan'],
  ['mortgage', 'Mortgage'], ['auto', 'Auto Loan'],
  ['education', 'Education Loan'], ['emergency', 'Emergency Loan'],
];
const PURPOSES = [
  ['personal', 'Personal'], ['business', 'Business'], ['medical', 'Medical'],
  ['education', 'Education'], ['home_improvement', 'Home Improvement'],
  ['debt_consolidation', 'Debt Consolidation'], ['other', 'Other'],
];

const KYC_MESSAGES = {
  not_submitted: {
    icon: '🪪',
    title: 'KYC Verification Required',
    body: 'You need to complete identity verification before you can apply for a loan.',
    cta: 'Complete KYC Now',
    ctaTo: '/profile',
    type: 'warning',
  },
  pending: {
    icon: '⏳',
    title: 'KYC Submission Under Review',
    body: 'Your KYC documents have been submitted and are being reviewed by our team. You\'ll be notified once approved.',
    cta: 'Go to Profile',
    ctaTo: '/profile',
    type: 'info',
  },
  under_review: {
    icon: '🔍',
    title: 'KYC Under Review',
    body: 'Our compliance team is currently reviewing your KYC documents. This usually takes 1–2 business days.',
    cta: 'Go to Profile',
    ctaTo: '/profile',
    type: 'info',
  },
  rejected: {
    icon: '❌',
    title: 'KYC Rejected',
    body: 'Your KYC verification was rejected. Please resubmit your documents with the correct information.',
    cta: 'Resubmit KYC',
    ctaTo: '/profile',
    type: 'error',
  },
  resubmit: {
    icon: '🔄',
    title: 'Resubmission Required',
    body: 'Our team has requested additional information or corrected documents. Please resubmit your KYC.',
    cta: 'Resubmit KYC',
    ctaTo: '/profile',
    type: 'warning',
  },
};

function KYCGate({ kycStatus, rejectionReason }) {
  const info = KYC_MESSAGES[kycStatus] || KYC_MESSAGES.not_submitted;

  const colors = {
    warning: { bg: 'rgba(255,153,0,0.08)', border: 'rgba(255,153,0,0.25)', icon: '#ff9900', text: '#ffc04d' },
    info:    { bg: 'rgba(91,142,255,0.08)', border: 'rgba(91,142,255,0.25)', icon: '#5B8EFF', text: '#7ea8ff' },
    error:   { bg: 'rgba(255,71,87,0.08)',  border: 'rgba(255,71,87,0.25)',  icon: '#FF4757', text: '#ff7e8a' },
  };
  const c = colors[info.type];

  return (
    <div style={{ maxWidth: 560 }}>
      <div className="page-header">
        <h1>Apply for a Loan</h1>
        <p>Submit your loan application</p>
      </div>

      <div style={{
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: 'var(--radius-lg)',
        padding: '2rem',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem', lineHeight: 1 }}>{info.icon}</div>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '0.5rem' }}>
          {info.title}
        </h2>
        <p style={{ color: 'var(--text-2)', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: rejectionReason ? '0.75rem' : '1.5rem' }}>
          {info.body}
        </p>

        {rejectionReason && (
          <div style={{
            background: 'rgba(255,71,87,0.08)', border: '1px solid rgba(255,71,87,0.2)',
            borderRadius: 'var(--radius)', padding: '0.75rem 1rem',
            marginBottom: '1.5rem', textAlign: 'left',
          }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#ff7e8a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rejection reason</span>
            <p style={{ color: 'var(--text-2)', fontSize: '0.875rem', marginTop: '0.25rem' }}>{rejectionReason}</p>
          </div>
        )}

        {info.cta && (
          <Link to={info.ctaTo} className="btn btn-primary btn-lg">
            {info.cta}
          </Link>
        )}

        <div style={{ marginTop: '1.25rem' }}>
          <Link to="/dashboard" style={{ fontSize: '0.875rem', color: 'var(--text-2)' }}>
            ← Back to dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}

function computeDefaultPaymentOptions(tenureMonths) {
  const options = [
    { payments: 1, label: `Single payment (${tenureMonths * 30} days)` },
  ];
  if (tenureMonths >= 1) {
    options.push({ payments: 2, label: `2 payments (${tenureMonths * 15} days interval)` });
  }
  if (tenureMonths >= 2) {
    options.push({ payments: 4, label: `4 payments (${Math.floor(tenureMonths * 15 / 2)} days interval)` });
  }
  return options;
}

function getPaymentOptions(product, tenureMonths) {
  if (product && product.payment_options) {
    const key = String(tenureMonths);
    if (product.payment_options[key] && product.payment_options[key].length > 0) {
      return product.payment_options[key];
    }
  }
  return computeDefaultPaymentOptions(tenureMonths);
}

function fmt(n) {
  return Number(n).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StepIndicator({ step }) {
  const steps = ['Loan Details', 'Payment Schedule'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: '1.75rem', maxWidth: 640 }}>
      {steps.map((label, idx) => {
        const num = idx + 1;
        const active = step === num;
        const done = step > num;
        return (
          <div key={num} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: done ? '#00C878' : active ? '#5B8EFF' : 'rgba(255,255,255,0.08)',
                border: `2px solid ${done ? '#00C878' : active ? '#5B8EFF' : 'rgba(255,255,255,0.15)'}`,
                fontSize: '0.8rem', fontWeight: 700,
                color: (done || active) ? '#fff' : 'var(--text-3)',
                transition: 'all 0.2s',
              }}>
                {done ? '✓' : num}
              </div>
              <span style={{ fontSize: '0.7rem', color: active ? 'var(--text-1)' : 'var(--text-3)', fontWeight: active ? 600 : 400, whiteSpace: 'nowrap' }}>
                {label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? '#00C878' : 'rgba(255,255,255,0.1)', margin: '0 4px', marginBottom: 20, transition: 'background 0.3s' }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function LoanApplication() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useAuth();
  const preProduct = location.state?.product ?? null;

  // Step management
  const [step, setStep] = useState(1);

  // Step 1 form state
  const [form, setForm] = useState({
    loan_type: preProduct?.loan_type || 'personal',
    purpose: 'personal',
    requested_amount: '',
    requested_tenure_months: '',
    notes: '',
  });

  // Step 2 state
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [selectedPaymentCount, setSelectedPaymentCount] = useState(1);
  const [schedulePreview, setSchedulePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  // Step 3 state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const alreadyVerified = user?.is_kyc_verified === true;
  const { data: kycData, isLoading: kycLoading } = useQuery({
    queryKey: ['kyc-status'],
    queryFn: () => kycService.getStatus().then(r => r.data.data),
    enabled: !alreadyVerified,
  });

  const { data: loansData, isLoading: loansLoading } = useQuery({
    queryKey: ['my-loans'],
    queryFn: () => loanService.getMyLoans().then(r => r.data.data),
  });

  const PENDING_STATUSES = ['submitted', 'under_review', 'credit_check', 'offer_sent', 'offer_accepted'];
  const pendingLoan = (loansData?.results || []).find(l => PENDING_STATUSES.includes(l.status));

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const fetchPreview = useCallback(async (amount, tenure, paymentCount) => {
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const payload = {
        amount: parseFloat(amount),
        tenure_months: parseInt(tenure, 10),
        payment_count: paymentCount,
      };
      if (preProduct) {
        payload.loan_product_id = preProduct.id;
        payload.interest_rate = parseFloat(preProduct.effective_rate) || parseFloat(preProduct.base_interest_rate) || 0;
      } else {
        payload.interest_rate = 10;
      }
      const res = await loanService.previewSchedule(payload);
      setSchedulePreview(res.data.data);
    } catch (err) {
      setPreviewError(err.response?.data?.message || 'Failed to load schedule preview.');
    } finally {
      setPreviewLoading(false);
    }
  }, [preProduct]);

  const handleCalculate = async e => {
    e.preventDefault();
    if (!form.requested_amount || !form.requested_tenure_months) return;

    const tenure = parseInt(form.requested_tenure_months, 10);
    const options = getPaymentOptions(preProduct, tenure);
    setPaymentOptions(options);

    const firstCount = options[0]?.payments ?? 1;
    setSelectedPaymentCount(firstCount);
    setStep(2);
    await fetchPreview(form.requested_amount, tenure, firstCount);
  };

  const handlePaymentOptionChange = async (count) => {
    setSelectedPaymentCount(count);
    await fetchPreview(form.requested_amount, form.requested_tenure_months, count);
  };

  const handleConfirm = async () => {
    setLoading(true); setError('');
    try {
      const payload = {
        ...form,
        requested_amount: parseFloat(form.requested_amount),
        requested_tenure_months: parseInt(form.requested_tenure_months, 10),
        payment_count: selectedPaymentCount,
      };
      if (preProduct) {
        payload.loan_product = preProduct.id;
        payload.loan_type    = preProduct.loan_type;
      }
      await loanService.apply(payload);
      navigate('/loans', { state: { success: 'Application submitted successfully!' } });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit application.');
      setLoading(false);
    }
  };

  if ((!alreadyVerified && kycLoading) || loansLoading) return <LoadingSpinner />;

  if (pendingLoan) {
    return (
      <div style={{ maxWidth: 560 }}>
        <div className="page-header">
          <h1>Apply for a Loan</h1>
          <p>Submit your loan application</p>
        </div>
        <div style={{
          background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.25)',
          borderRadius: 'var(--radius-lg)', padding: '2rem', textAlign: 'center',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', lineHeight: 1 }}>⏳</div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '0.5rem' }}>
            Application In Progress
          </h2>
          <p style={{ color: 'var(--text-2)', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1rem' }}>
            You already have a pending application <strong style={{ color: 'var(--text-1)' }}>{pendingLoan.application_number}</strong> with
            status <strong style={{ color: '#F5A623' }}>{pendingLoan.status.replace(/_/g, ' ')}</strong>.
            You cannot submit a new application until the current one is resolved.
          </p>
          <Link to="/loans" className="btn btn-primary">View My Applications</Link>
          <div style={{ marginTop: '1.25rem' }}>
            <Link to="/dashboard" style={{ fontSize: '0.875rem', color: 'var(--text-2)' }}>
              ← Back to dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const kycStatus = alreadyVerified ? 'approved' : (kycData?.status || 'not_submitted');
  if (kycStatus !== 'approved') {
    return <KYCGate kycStatus={kycStatus} rejectionReason={kycData?.rejection_reason} />;
  }

  const minAmount  = preProduct ? parseFloat(preProduct.min_amount)  || 1000      : 1000;
  const maxAmount  = preProduct ? parseFloat(preProduct.max_amount)  || 5_000_000  : 5_000_000;
  const minTenure  = preProduct ? preProduct.min_tenure_months || 3               : 3;
  const maxTenure  = preProduct ? preProduct.max_tenure_months || 60              : 60;

  const effectiveRate = preProduct ? parseFloat(preProduct.effective_rate) || 0 : 0;
  const baseRate      = preProduct ? parseFloat(preProduct.base_interest_rate) || 0 : 0;
  const hasDiscount   = preProduct && effectiveRate < baseRate;

  return (
    <div>
      <div className="page-header">
        <h1>Apply for a Loan</h1>
        <p>Fill in the details below to submit your application</p>
      </div>

      <StepIndicator step={step} />

      {/* Pre-selected product banner */}
      {preProduct && (
        <div style={{
          maxWidth: 640, marginBottom: '1.25rem',
          background: 'linear-gradient(135deg, rgba(91,142,255,0.12) 0%, rgba(6,201,255,0.06) 100%)',
          border: '1px solid rgba(91,142,255,0.28)',
          borderRadius: 'var(--radius-lg)', padding: '1rem 1.25rem',
          display: 'flex', alignItems: 'center', gap: '0.9rem',
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, flexShrink: 0,
            background: 'linear-gradient(135deg, #5B8EFF 0%, #06C9FF 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem',
          }}>🏦</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-1)' }}>
              {preProduct.name}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>
              Rate:&nbsp;
              {hasDiscount && (
                <span style={{ textDecoration: 'line-through', color: 'var(--text-3)', marginRight: '0.3rem' }}>
                  {baseRate.toFixed(1)}%
                </span>
              )}
              <span style={{ color: '#5B8EFF', fontWeight: 700 }}>{effectiveRate.toFixed(1)}% /month</span>
              {hasDiscount && (
                <span style={{ color: '#00C878', marginLeft: '0.5rem', fontSize: '0.75rem' }}>
                  🏷️ {(baseRate - effectiveRate).toFixed(2)}% off
                </span>
              )}
            </div>
          </div>
          {step === 1 && (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}
              onClick={() => navigate('/loans/credit-check')}
            >
              Change
            </button>
          )}
        </div>
      )}

      {/* ── STEP 1: Amount + Tenure ── */}
      {step === 1 && (
        <div className="card" style={{ maxWidth: '640px' }}>
          {!preProduct && (
            <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
              <span>✓</span>
              <span>Your identity has been verified. You're eligible to apply.</span>
            </div>
          )}
          <form onSubmit={handleCalculate}>
            {!preProduct && (
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Loan Type</label>
                  <select className="form-input" name="loan_type" value={form.loan_type} onChange={handleChange}>
                    {LOAN_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Purpose</label>
                  <select className="form-input" name="purpose" value={form.purpose} onChange={handleChange}>
                    {PURPOSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              </div>
            )}

            {preProduct && (
              <div className="form-group">
                <label className="form-label">Purpose</label>
                <select className="form-input" name="purpose" value={form.purpose} onChange={handleChange}>
                  {PURPOSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            )}

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Loan Amount (₦)</label>
                <input
                  className="form-input" type="number" name="requested_amount"
                  value={form.requested_amount} onChange={handleChange}
                  min={minAmount} max={maxAmount} required
                  placeholder={`e.g. ${Math.round(maxAmount / 2).toLocaleString()}`}
                />
                {preProduct && (
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-3)', marginTop: '0.25rem', display: 'block' }}>
                    Range: ₦{minAmount.toLocaleString()} – ₦{maxAmount.toLocaleString()}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Tenure (months)</label>
                <select
                  className="form-input" name="requested_tenure_months"
                  value={form.requested_tenure_months} onChange={handleChange} required
                >
                  <option value="">Select tenure…</option>
                  {Array.from({ length: maxTenure - minTenure + 1 }, (_, i) => minTenure + i).map(m => (
                    <option key={m} value={m}>{m} month{m !== 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Additional Notes (optional)</label>
              <textarea
                className="form-input" name="notes" value={form.notes} onChange={handleChange}
                rows={3} placeholder="Tell us more about why you need this loan…"
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
              <button type="submit" className="btn btn-primary btn-lg">
                Calculate Schedule
              </button>
              <button type="button" className="btn btn-ghost btn-lg" onClick={() => navigate('/dashboard')}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── STEP 2: Payment Options + Schedule Breakdown ── */}
      {step === 2 && (
        <div style={{ maxWidth: 720 }}>
          {previewError && (
            <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
              <span>⚠</span><span>{previewError}</span>
            </div>
          )}

          {/* Payment option cards */}
          <div className="card" style={{ marginBottom: '1.25rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '1rem' }}>
              Select Payment Option
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {paymentOptions.map(opt => {
                const isSelected = selectedPaymentCount === opt.payments;
                return (
                  <label
                    key={opt.payments}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      padding: '0.875rem 1rem',
                      borderRadius: 'var(--radius)',
                      border: `2px solid ${isSelected ? '#5B8EFF' : 'var(--border)'}`,
                      background: isSelected ? 'rgba(91,142,255,0.08)' : 'var(--surface)',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    <input
                      type="radio"
                      name="payment_count"
                      value={opt.payments}
                      checked={isSelected}
                      onChange={() => handlePaymentOptionChange(opt.payments)}
                      style={{ accentColor: '#5B8EFF' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-1)' }}>
                        {opt.label}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-2)', marginTop: '0.15rem' }}>
                        {opt.payments} installment{opt.payments > 1 ? 's' : ''}
                      </div>
                    </div>
                    {isSelected && schedulePreview && (
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 700, color: '#5B8EFF', fontSize: '1rem' }}>
                          ₦{fmt(schedulePreview.installment_amount)}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>per installment</div>
                      </div>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Schedule breakdown */}
          {previewLoading ? (
            <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ display: 'inline-block', width: 24, height: 24, borderRadius: '50%', border: '3px solid rgba(91,142,255,0.25)', borderTopColor: '#5B8EFF', animation: 'spin .6s linear infinite' }} />
              <p style={{ color: 'var(--text-2)', marginTop: '0.75rem', fontSize: '0.875rem' }}>Loading schedule…</p>
            </div>
          ) : schedulePreview ? (
            <div className="card">
              {/* Summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem', padding: '1rem', background: 'var(--surface-alt)', borderRadius: 'var(--radius)' }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>Principal</div>
                  <div style={{ fontWeight: 700, color: 'var(--text-1)', fontSize: '1rem' }}>₦{fmt(schedulePreview.principal)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>Total Interest</div>
                  <div style={{ fontWeight: 700, color: '#FF6B35', fontSize: '1rem' }}>₦{fmt(schedulePreview.total_interest)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>Total Repayment</div>
                  <div style={{ fontWeight: 700, color: '#00C878', fontSize: '1rem' }}>₦{fmt(schedulePreview.total_repayment)}</div>
                </div>
              </div>

              {/* Schedule table */}
              <h4 style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '0.75rem' }}>Repayment Schedule</h4>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['#', 'Due Date', 'Principal', 'Interest', 'Amount Due', 'Balance'].map(h => (
                        <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: h === '#' ? 'center' : 'right', color: 'var(--text-3)', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {schedulePreview.schedule.map((row, idx) => (
                      <tr key={row.installment} style={{ borderBottom: idx < schedulePreview.schedule.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center', fontWeight: 700, color: '#5B8EFF' }}>{row.installment}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: 'var(--text-2)' }}>{row.due_date}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: 'var(--text-1)' }}>₦{fmt(row.principal_due)}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: '#FF6B35' }}>₦{fmt(row.interest_due)}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', fontWeight: 700, color: 'var(--text-1)' }}>₦{fmt(row.amount_due)}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', color: 'var(--text-3)' }}>₦{fmt(row.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {/* Charges breakdown */}
          {schedulePreview?.charges && (
            <div className="card" style={{ marginBottom: '1.25rem' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-1)', marginBottom: '1rem' }}>
                Charges &amp; Terms
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                {[
                  {
                    label: 'Processing Fee',
                    value: `₦${fmt(schedulePreview.charges.processing_fee)} (${schedulePreview.charges.processing_fee_rate_pct}%)`,
                    sub: 'One-time fee, deducted at disbursement',
                    color: '#F5A623',
                  },
                  {
                    label: 'Net Disbursement',
                    value: `₦${fmt(schedulePreview.net_disbursement)}`,
                    sub: 'Amount credited to your account',
                    color: '#00C896',
                  },
                  {
                    label: 'Late Payment Penalty',
                    value: schedulePreview.charges.late_penalty_type === 'fixed'
                      ? `₦${fmt(schedulePreview.charges.late_penalty_value)} flat`
                      : `${schedulePreview.charges.late_penalty_value}% of overdue amount`,
                    sub: 'Applied per overdue installment',
                    color: '#FF9900',
                  },
                  {
                    label: 'Default Charge',
                    value: `${schedulePreview.charges.default_charge_rate}% p.m.`,
                    sub: `Kicks in after ${schedulePreview.charges.default_threshold_days} days overdue`,
                    color: '#FF4757',
                  },
                ].map(({ label, value, sub, color }) => (
                  <div key={label} style={{ padding: '0.75rem', background: 'var(--surface-alt)', borderRadius: 'var(--radius)', borderLeft: `3px solid ${color}` }}>
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>{label}</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 700, color }}>{value}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="alert alert-error" style={{ marginTop: '1rem' }}>
              <span>⚠</span><span>{error}</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
            <button
              className="btn btn-primary btn-lg"
              onClick={handleConfirm}
              disabled={loading || previewLoading || !schedulePreview}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin .6s linear infinite', display: 'inline-block' }} />
                  Submitting…
                </span>
              ) : 'Confirm & Apply'}
            </button>
            <button className="btn btn-ghost btn-lg" onClick={() => setStep(1)}>
              ← Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
